import React from "react";
import { DocumentData } from "../xml-document-parser.service";
import { AddressBlock } from "./party-info-section.component";
import { ServicesTable, ServicesSummary } from "./services-table.component";
import { QrCodeIcon } from "lucide-react";

const PARTNER_TYPE_LABELS: Record<string, string> = {
	employer: "Arbeitgeber",
	referrer: "Zuweiser",
	service_provider: "Leistungserbringer",
	primary_clinician: "Erstbehandler",
	lead_doctor: "Fallführender Arzt",
	assistant_physician: "Assistenzarzt",
	senior_physician: "Oberarzt",
	chief_physician: "Chefarzt",
	surgeon: "Operateur",
	anaesthetist: "Anästhesist",
	consultant_doctor: "Konsiliararzt",
	internist: "Internist",
	"co-care": "Mitbehandlung",
	radiologist: "Radiologe",
	nuclear_medicine: "Nuklearmedizin",
	other: "Andere"
};

const TIERS_LABELS: Record<string, string> = {
	tiers_garant: "TG",
	tiers_soldant: "TS",
	tiers_payant: "TP"
};

interface LsDocumentViewerProps {
	data: DocumentData;
}

export const LsDocumentViewer: React.FC<LsDocumentViewerProps> = ({ data }) => {
	const formatAmount = (amount: number | undefined): string => {
		if (amount === undefined || amount === null) return '0.00';
		return amount.toFixed(2);
	};

	const isPatientInvoice = data.tiersType === 'tiers_garant';
	const release = data.release || (data.version === '450' ? '4.50' : '5.00');

	return (
		<div className="font-sans text-black">
			{/* First Page - Patient Invoice with QR Code */}
			{isPatientInvoice && (
				<div className="mb-8">
					{/* Header */}
					<div className="flex justify-between items-start mb-6">
						<div>
							<h1 className="text-xl font-bold">Patientenrechnung mit QR-Code</h1>
							<div className="flex items-center gap-2 text-xs mt-1">
								<span>Release {release}/QR/de</span>
								<QrCodeIcon className="w-6 h-6" />
								<span className="text-xs">Für Ihre Unterlagen</span>
							</div>
						</div>
						{/* WebStamp placeholder - TODO: Implement actual barcode generation 
						    WebStamp is a payment validation barcode that should contain encoded payment reference data.
						    Requires a barcode library and understanding of Swiss WebStamp format specification. */}
						<div className="border border-gray-300 p-2 text-xs">
							<div className="text-center font-mono">WebStamp</div>
							<div className="text-center mt-1">||||| ||||| |||||</div>
							<div className="text-center text-[8px] text-gray-400 mt-1">(Dies ist ein Platzhalter. Der Code wird hier nicht generiert.)</div>
						</div>
					</div>

					{/* Address blocks */}
					<div className="grid grid-cols-3 gap-6 mb-6">
						<div>
							<h3 className="font-semibold text-sm mb-2">Rechnungsadressat</h3>
							<AddressBlock party={data.debitor || data.guarantor || data.patient} />
						</div>
						<div>
							<h3 className="font-semibold text-sm mb-2">RechnungsstellerIn</h3>
							<AddressBlock party={data.biller} />
							{data.biller?.phone && <div className="text-sm mt-1">Tel. {data.biller.phone}</div>}
							{data.biller?.email && <div className="text-sm">{data.biller.email}</div>}
						</div>
						<div>
							<h3 className="font-semibold text-sm mb-2">LeistungserbringerIn</h3>
							<AddressBlock party={data.provider} />
							{data.provider?.phone && <div className="text-sm mt-1">Tel. {data.provider.phone}</div>}
							{data.provider?.email && <div className="text-sm">{data.provider.email}</div>}
						</div>
					</div>

					{/* Patient info */}
					<div className="border-t pt-4 mb-6">
						<h3 className="font-semibold text-sm mb-2">PatientIn</h3>
						<AddressBlock party={data.patient} />
						<div className="text-sm mt-2">
							Geburtsdatum {data.patient?.birthdate || ''} · {data.patient?.gender === 'female' ? 'Frau / F' : data.patient?.gender === 'male' ? 'Mann / M' : ''} · {data.patient?.patientId || ''}
						</div>
					</div>

					{/* Remark */}
					{data.remark && (
						<div className="bg-gray-50 p-3 mb-6 text-sm">
							{data.remark}
						</div>
					)}

					{/* Invoice data */}
					<div className="grid grid-cols-2 gap-8 mb-6">
						<div>
							<h3 className="font-semibold text-sm mb-2">Rechnungs-Daten</h3>
							<table className="text-sm">
								<tbody>
									<tr>
										<td className="pr-4">MwSt.-Nummer:</td>
										<td>{data.vat?.number || ''}</td>
									</tr>
									<tr>
										<td className="pr-4">Rechnungs-Datum:</td>
										<td>{data.date}</td>
									</tr>
									<tr>
										<td className="pr-4">Rechnungs-Nummer:</td>
										<td>{data.documentNumber}</td>
									</tr>
									<tr>
										<td className="pr-4">Behandlung:</td>
										<td>{data.treatment?.dateBegin} - {data.treatment?.dateEnd}</td>
									</tr>
								</tbody>
							</table>
						</div>

						<div>
							<h3 className="font-semibold text-sm mb-2">Bereich</h3>
							<table className="text-sm w-full">
								<tbody>
									<tr>
										<td>Medizinisch:</td>
										<td className="text-right">
											{data.medicalAmount ? formatAmount(data.medicalAmount) : `${formatAmount(data.total)} (Nutze "Total" als Ersatz)`}
										</td>
									</tr>
									<tr>
										<td>Medikamente:</td>
										<td className="text-right">{data.medicationAmount ? formatAmount(data.medicationAmount) : 'Keine Angabe'}</td>
									</tr>
									<tr>
										<td>Labor:</td>
										<td className="text-right">{data.laboratoryAmount ? formatAmount(data.laboratoryAmount) : 'Keine Angabe'}</td>
									</tr>
									<tr>
										<td>MiGel:</td>
										<td className="text-right">{data.migelAmount ? formatAmount(data.migelAmount) : 'Keine Angabe'}</td>
									</tr>
									<tr>
										<td>Übrige:</td>
										<td className="text-right">{data.otherAmount ? formatAmount(data.otherAmount) : 'Keine Angabe'}</td>
									</tr>
									<tr className="border-t font-semibold">
										<td>Gesamttotal:</td>
										<td className="text-right">{formatAmount(data.total)}</td>
									</tr>
									<tr>
										<td>Anzahlung:</td>
										<td className="text-right">{data.prepaidAmount ? formatAmount(data.prepaidAmount) : 'Keine Angabe'}</td>
									</tr>
									<tr className="font-semibold">
										<td>Fälliger Betrag:</td>
										<td className="text-right">
											{data.amountDue ? formatAmount(data.amountDue) : `${formatAmount(data.total)} (Nutze "Total" als Ersatz)`}
										</td>
									</tr>
								</tbody>
							</table>
							<div className="mt-2 text-sm">
								<table className="w-full">
									<tbody>
										<tr>
											<td>MwSt-Satz/%</td>
											<td className="text-right">{data.vat?.rate?.toFixed(2) || '0.00'}</td>
										</tr>
										<tr>
											<td>MwSt/CHF</td>
											<td className="text-right">{formatAmount(data.vat?.amount)}</td>
										</tr>
										<tr className="font-semibold">
											<td>MwSt-Total:</td>
											<td className="text-right">{formatAmount(data.vat?.amount)}</td>
										</tr>
									</tbody>
								</table>
							</div>
							<div className="text-sm mt-2">
								{/* Default to 30 days if no payment terms specified */}
								{/* 5.00 moved the payment period from the tiers element to the QR payment part */}
								{(() => {
									const period = data.paymentTerms || data.esr?.paymentPeriod;
									return period ? `Zahlbar innert ${period.replace('P', '').replace('D', '')} Tagen rein netto` : 'Keine Angabe';
								})()}
							</div>
						</div>
					</div>

					{/* QR Payment slip mockup */}
					<div className="border-2 border-black p-4">
						<div className="grid grid-cols-2 gap-8">
							<div>
								<h4 className="font-bold text-sm mb-2">Empfangsschein</h4>
								<div className="text-xs space-y-1">
									<div>Konto / Zahlbar an</div>
									{/* Display fallback IBAN if none provided */}
									<div className="font-mono">{data.esr?.iban || 'Keine Angabe'}</div>
									{data.esr?.creditor && <AddressBlock party={data.esr.creditor} />}
									<div className="mt-2">Referenz</div>
									{/* Display fallback reference number if none provided */}
									<div className="font-mono">{data.esr?.referenceNumber || 'Keine Angabe'}</div>
									<div className="mt-2">Zahlbar durch</div>
									<AddressBlock party={data.debitor || data.patient} />
									<div className="grid grid-cols-2 gap-4 mt-4">
										<div>
											<div>Währung</div>
											<div className="font-bold">{data.currency}</div>
										</div>
										<div>
											<div>Betrag</div>
											<div className="font-bold">{data.amountDue ? formatAmount(data.amountDue) : `${formatAmount(data.total)} (Nutze "Total" als Ersatz)`}</div>
										</div>
									</div>
								</div>
							</div>
							<div>
								<h4 className="font-bold text-sm mb-2">Zahlteil</h4>
								{/* QR Code placeholder - actual QR generation not implemented */}
								<div className="flex justify-center items-center h-32 bg-gray-100 border">
									<QrCodeIcon className="w-24 h-24 text-gray-400" />
								</div>
								<div className="text-xs mt-2">
									<div>Währung: {data.currency}</div>
									<div>Betrag: {data.amountDue ? formatAmount(data.amountDue) : `${formatAmount(data.total)} (Nutze "Total" als Ersatz)`}</div>
									{data.esr?.paymentReason && (
										<div className="mt-2">
											<div>Zusätzliche Informationen</div>
											<div className="text-xs">{data.esr.paymentReason}</div>
										</div>
									)}
									{(data.esr?.alternativeScheme1 || data.esr?.alternativeScheme2) && (
										<div className="mt-2">
											<div>Alternative Verfahren</div>
											{data.esr.alternativeScheme1 && <div className="text-xs break-all">{data.esr.alternativeScheme1}</div>}
											{data.esr.alternativeScheme2 && <div className="text-xs break-all">{data.esr.alternativeScheme2}</div>}
										</div>
									)}
									{data.esr?.subtype && <div className="mt-2 text-gray-600">Zahlungsart: {data.esr.subtype}</div>}
									{data.esr?.paymentPeriod && (
										<div className="text-gray-600">Zahlungsfrist: {data.esr.paymentPeriod.replace('P', '').replace('D', '')} Tage</div>
									)}
								</div>
							</div>
						</div>
					</div>
				</div>
			)}

			{/* Second Page - Rückforderungsbeleg */}
			<div className="break-before-page">
				{/* Header */}
				<div className="flex justify-between items-start mb-4">
					<h1 className="text-xl font-bold">Rückforderungsbeleg</h1>
					<div className="text-right text-xs">
						<div>Release {release}/General/de</div>
						<div>Der Versicherung zustellen</div>
						<div>Seite: 1</div>
					</div>
				</div>

				{/* Document info */}
				<div className="border border-gray-400 p-2 mb-4 text-xs">
					<table className="w-full">
						<tbody>
							<tr>
								<td className="font-semibold w-32">Dokument</td>
								<td>Identifikation: {data.timestamp || ''} / {data.guid || ''}</td>
							</tr>
							<tr>
								<td className="font-semibold">Rechnungssteller</td>
								<td>
									GLN-Nr.(B): {data.biller?.gln || data.biller?.eanParty || ''} {data.biller?.companyName} · {data.biller?.department}
								</td>
							</tr>
							<tr>
								<td></td>
								<td>
									ZSR-Nr.(B): {data.biller?.zsr || ''} {data.biller?.street} · {data.biller?.zip} {data.biller?.city}
								</td>
							</tr>
						</tbody>
					</table>
				</div>

				{/* Patient info grid */}
				<div className="grid grid-cols-3 gap-4 mb-4 text-xs">
					<div className="border border-gray-400 p-2">
						<div className="font-semibold mb-2">Patient</div>
						<table className="w-full">
							<tbody>
								<tr><td className="pr-2">Name</td><td>{data.patient?.familyName}</td></tr>
								<tr><td className="pr-2">Vorname</td><td>{data.patient?.givenName}</td></tr>
								<tr><td className="pr-2">Strasse</td><td>{data.patient?.street}</td></tr>
								<tr><td className="pr-2">PLZ</td><td>{data.patient?.zip}</td></tr>
								<tr><td className="pr-2">Ort</td><td>{data.patient?.city}</td></tr>
								<tr><td className="pr-2">Geburtsdatum</td><td>{data.patient?.birthdate}</td></tr>
								<tr><td className="pr-2">Geschlecht</td><td>{data.patient?.gender === 'female' ? 'Frau / F' : data.patient?.gender === 'male' ? 'Mann / M' : ''}</td></tr>
								<tr><td className="pr-2">Falldatum</td><td>{data.caseDate || data.date}</td></tr>
								<tr><td className="pr-2">Fall-Nr.</td><td>{data.caseId || ''}</td></tr>
								<tr><td className="pr-2">AHV-Nr.</td><td>{data.patient?.ahvNumber || ''}</td></tr>
								<tr><td className="pr-2">VEKA-Nr.</td><td>{data.patient?.vekaNumber || data.patient?.cardId || ''}</td></tr>
								<tr><td className="pr-2">Versicherten-Nr.</td><td>{data.patient?.insuredNumber || ''}</td></tr>
								<tr><td className="pr-2">Kanton</td><td>{data.treatment?.canton || ''}</td></tr>
								<tr><td className="pr-2">Kopie</td><td>{data.isCopy ? 'ja' : 'nein'}</td></tr>
								<tr><td className="pr-2">Vergütungsart</td><td>{data.tiersType ? TIERS_LABELS[data.tiersType] : ''}</td></tr>
								<tr><td className="pr-2">Gesetz</td><td>{data.law || data.lawType || ''}</td></tr>
								<tr><td className="pr-2">Behandlung</td><td>{data.treatment?.dateBegin} - {data.treatment?.dateEnd}</td></tr>
								<tr><td className="pr-2">Behandlungsart</td><td>{data.treatment?.treatmentType || 'Keine Angabe'}</td></tr>
								<tr><td className="pr-2">Behandlungsgrund</td><td>{data.treatment?.reason === 'disease' ? 'Krankheit' : data.treatment?.reason === 'accident' ? 'Unfall' : data.treatment?.reason}</td></tr>
							</tbody>
						</table>
					</div>

					<div className="border border-gray-400 p-2">
						<div className="mb-2">
							<div>GLN-Nr.</div>
							<AddressBlock party={data.debitor || data.patient} />
						</div>
						<div className="mt-4">
							<table className="w-full">
								<tbody>
									{data.creditRequestId && (
										<tr><td>KoGu-Datum/-Nr.</td><td>{data.creditRequestDate} / {data.creditRequestId}</td></tr>
									)}
									<tr><td>Rechnungs-Datum/-Nr.</td><td>{data.date} / {data.documentNumber}</td></tr>
									{(data.reminderDate || data.reminderNumber) && (
										<tr><td>Mahn-Datum/-Nr.</td><td>{data.reminderDate || ''} / {data.reminderNumber || ''}</td></tr>
									)}
								</tbody>
							</table>
						</div>
					</div>

					<div className="border border-gray-400 p-2">
						<div className="font-semibold mb-2">Rolle/Ort</div>
						<div>{data.role === 'physician' ? 'Arzt/Ärztin' : data.role} · {data.place === 'practice' ? 'Praxis' : data.place} · {data.roleTitle || ''}</div>
						<div className="mt-2 font-semibold">Leistungserbringer</div>
						<div>GLN-Nr.(P) {data.provider?.gln || data.provider?.eanParty || ''} {data.provider?.companyName}</div>
						<div>ZSR-Nr.(P) {data.provider?.zsr || ''}</div>
						<div>{data.provider?.street} · {data.provider?.zip} {data.provider?.city}</div>
						{data.provider?.phone && <div>{data.provider.phone}</div>}
						{data.provider?.email && <div>{data.provider.email}</div>}
					</div>
				</div>

				{/* Diagnosis */}
				{data.treatment?.diagnoses && data.treatment.diagnoses.length > 0 && (
					<div className="mb-4 text-xs">
						{data.treatment.diagnoses.map((diagnosis, index) => (
							<div key={index} className={index > 0 ? "mt-2" : ""}>
								<div className="font-semibold">Diagnose {diagnosis.code || ''}</div>
								<div>{diagnosis.text}</div>
								{diagnosis.type && diagnosis.type !== 'freetext' && (
									<div className="text-gray-600 mt-1">Typ: {diagnosis.type}</div>
								)}
							</div>
						))}
					</div>
				)}

				{/* Remark */}
				{data.remark && (
					<div className="mb-4 text-xs">
						<div className="font-semibold">Bemerkung</div>
						<div>{data.remark}</div>
					</div>
				)}

				{/* Partners */}
				{data.partners && data.partners.length > 0 && (
					<div className="mb-4 text-xs">
						<div className="font-semibold mb-1">Partner</div>
						<table className="w-full">
							<thead>
								<tr className="border-b">
									<th className="text-left">#</th>
									<th className="text-left">GLN-/ZSR-/Sektion-Nr.</th>
									<th className="text-left">Adresse</th>
								</tr>
							</thead>
							<tbody>
								{data.partners.map((partner, index) => {
									const party = partner.person || partner.company;
									const address = [
										party?.title,
										party?.name,
										party?.department,
										party?.street,
										[party?.zip, party?.city].filter(Boolean).join(' ')
									].filter(Boolean).join(' · ');

									return (
										<tr key={`${partner.type}-${index}`}>
											<td>{index + 1} - {partner.typeTitle || PARTNER_TYPE_LABELS[partner.type] || partner.type}</td>
											<td>{[partner.gln, partner.zsr].filter(Boolean).join(' / ')}</td>
											<td>{address}</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				)}

				{/* Attachments */}
				{data.documents && data.documents.length > 0 && (
					<div className="mb-4 text-xs">
						<div className="font-semibold mb-1">Beilagen</div>
						<ul className="list-disc pl-4">
							{data.documents.map((attachment, index) => (
								<li key={index}>
									{attachment.filename}
									{(attachment.documentType || attachment.title) && ` · ${attachment.documentType || attachment.title}`}
									{attachment.mimeType && ` · ${attachment.mimeType}`}
								</li>
							))}
						</ul>
					</div>
				)}

				{/* Services */}
				<div className="mb-4">
					<ServicesTable services={data.services} type="lieferschein" />
				</div>

				{/* Summary */}
				<div className="border-t-2 border-black pt-2">
					<div className="flex justify-between text-sm">
						<div>
							<div>Code {data.vat?.rate === 0 ? '0' : ''} Satz {formatAmount(data.vat?.rate || 0)} Betrag {formatAmount(data.subtotal)} MWSt {formatAmount(data.vat?.amount || 0)}</div>
							<div>MWSt.-Nr.: {data.vat?.number || ''}</div>
							<div>Währung: {data.currency}</div>
						</div>
						<div className="text-right">
							<div className="font-semibold">Gesamtbetrag: {formatAmount(data.total)}</div>
							<div className="font-semibold">Rechnungsbetrag: {data.amountDue ? formatAmount(data.amountDue) : `${formatAmount(data.total)} (Nutze "Total" als Ersatz)`}</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};